// #336 slice 5, brief §5.4b: the rule the «Regola» sheet writes when it
// opened on the recognized rule's own preset. That rule is the base: Apply
// untouched writes nothing, and a field changed keeps what the preset has no
// field for. Keep free of React and any UI import.
import type { EffortScale } from "./effortScale.js";
import type { ProgressionRule } from "./programProgression.js";
import { presetFieldsFor, type PresetId, type PresetValues } from "./programProgressionFields.js";
import { presetRule } from "./programProgressionPresets.js";

/** A recognized rule the sheet opened on with its own preset, and the fields
 *  it opened with (presetValuesFor). */
export type PresetBase = { rule: ProgressionRule; values: PresetValues };

const sameField = (a: PresetValues[string] | undefined, b: PresetValues[string] | undefined): boolean =>
  Array.isArray(a) && Array.isArray(b) ? a.length === b.length && a.every((x, i) => x === b[i]) : a === b;

/** The rule the sheet writes. On the recognized rule's own preset (`base`)
 *  that rule is the base: the fields change it, and what the preset has no
 *  field for (the test and deload effects, the steps inside the round, the
 *  rep range) is carried. Fields as they opened give `base.rule` itself, so
 *  Apply untouched writes nothing. No `base`: presetRule. */
export const presetRuleOn = (id: PresetId, v: PresetValues, scale: EffortScale, base?: PresetBase): ProgressionRule => {
  const fresh = presetRule(id, v, scale);
  if (!base) return fresh;
  const fields = presetFieldsFor(id, fresh.axis);
  if (fields.every((fl) => sameField(v[fl.key], base.values[fl.key]))) return base.rule;
  const b = base.rule;
  const has = (k: string) => fields.some((fl) => fl.key === k);
  const factor = has("deloadPct") ? fresh.deload?.factor : undefined;
  const deload = factor === undefined ? b.deload : { ...b.deload, factor };
  const perSet = has("repsSetStep") || has("backoffEffort") ? fresh.perSet : b.perSet;
  const range = fresh.threshold ? fresh.range : b.range;
  const out: ProgressionRule = { axis: fresh.axis, sets: fresh.sets, reps: fresh.reps, value: fresh.value };
  if (fresh.work) out.work = fresh.work;
  if (fresh.threshold) out.threshold = fresh.threshold;
  if (b.test) out.test = b.test;
  if (deload) out.deload = deload;
  if (perSet) out.perSet = perSet;
  if (range) out.range = range;
  // A ramp read with no cap shows the top it reaches: left there, still none.
  const bv = b.value;
  const fv = fresh.value;
  if (id === "effortRamp" && bv?.kind === "linear" && bv.cap === undefined && v.cap === base.values.cap && fv?.kind === "linear") {
    out.value = { kind: "linear", start: fv.start, step: fv.step };
  }
  return out;
};
